import * as React from "react";
import { Button, notification } from "antd";
import { v4 as uuidv4 } from "uuid";
import {
  formInitialize,
  formSet,
  formUninitialize,
  useCpFormAnyValueId,
  useCpFormSubmit,
} from "../../gvc/testNewForm/utils";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultIpSetInput, inputToIpSet } from "../utils";
import { IpSetInput } from "../../../schema/types/ipset/input";
import { IpSet } from "../../../schema/types/ipset/entity";
import { getValidator } from "../validator";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { IS_DEPLOYMENT_ENV_PROD } from "../../../envVariables";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { General } from "../general";
import { Spec } from "../spec/spec";
import { parseLink } from "../../../utils/linkParser/linkParser";

interface EmbeddedIpSetCreateProps {
  onDone: (itemLink: string, item: IpSet | undefined) => void | Promise<void>;
  onCancel: () => void;
  lockedLink?: string;
}

export const EmbeddedIpSetCreate: React.FC<EmbeddedIpSetCreateProps> = ({ onDone, onCancel, lockedLink }) => {
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    const input = getDefaultIpSetInput();
    if (lockedLink) {
      const parsed = parseLink(lockedLink, { useInputCtx: true });
      const isWorkload = parsed.kind === "workload";
      input.target = isWorkload ? "workload" : "gvc";
      if (isWorkload) {
        input.workload = {
          value: parsed.name,
          link: lockedLink,
        };
      } else {
        input.gvc = {
          value: parsed.name,
          link: lockedLink,
        };
      }
    }
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  const submitting = useCpFormAnyValueId(formId, "meta.submitting");

  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;

  const submit = useCpFormSubmit({
    id: formId,
    validationFn: validator,
    onSubmit: handleSubmit,
  });

  async function handleSubmit(input: IpSetInput) {
    try {
      const body = inputToIpSet(input);
      await request({
        method: "post",
        url: homeLink("ipset"),
        body,
      });
      const itemLink = resourceLink("ipset", body.name);
      notification.success({
        message: "Success",
        description: "IP Set is created",
      });
      let createdIpSet: IpSet | undefined;
      try {
        const { data } = await request({ url: itemLink });
        createdIpSet = data;
      } catch (e) {}
      onDone(itemLink, createdIpSet);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit }}>
      <CpFormValidator validationFn={validator} />
      <div className="flex flex-col gap-4">
        <General />
        <Spec lockedLink={lockedLink} />
        <CpTagsNew />
        <div className="flex flex-col gap-2">
          <Button type="primary" danger onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          {!IS_DEPLOYMENT_ENV_PROD && (
            <Button type="default" onClick={() => formSet(formId, "meta.debugging", true)}>
              Debug
            </Button>
          )}
          <Button type="primary" className="action" onClick={submit} loading={submitting} disabled={submitting}>
            Create
          </Button>
        </div>
      </div>
      <CpDebugModal inputToItemFn={inputToIpSet} kind="ipset" namePath="name" title="IP Set" />
    </CpFormContext.Provider>
  );
};
